// ---------------------------------------------------------------------------
// Estratto VERBATIM da TabContratti.tsx (Task #3874 — split interno Fase 2).
// Nessuna modifica logica: JSX spostato tale e quale, stato e handler restano
// nel componente padre e arrivano via props esplicite.
// ---------------------------------------------------------------------------
import type { Dispatch, SetStateAction } from 'react';
import {
  Plus, Trash2, Loader2, Clock, Calendar, CheckCircle, AlertTriangle,
} from 'lucide-react';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterQuote as Quote,
  CrmMasterContract as Contract,
  CrmMasterContractTemplate as ContractTemplate,
  CrmMasterQuotePaymentInfo as QuotePaymentInfo,
} from '@easyflow/shared';
import { ModalWrapper, SelectField } from '../shared';

type VoipNumber = { type: 'new' | 'portability'; number: string; operator_code?: string; operator_other?: string; codice_migrazione?: string; note?: string };
type VoipOperator = { code: string; label: string };
type Installment = { number: number; amount: number; due_date: string; description: string };
type AddonServices = { setup: boolean; formazione: boolean; assistenza: boolean };
type DateAdjustPending = { index: number; oldDate: string; newDate: string; deltaDays: number } | null;
type Frequency = 'monthly' | 'quarterly' | 'semiannual' | 'annual';

interface CreateContractModalProps {
  showCreate: boolean;
  setShowCreate: Dispatch<SetStateAction<boolean>>;
  selectedQuoteId: string;
  onQuoteSelected: (quoteId: string) => Promise<void>;
  acceptedQuotes: Quote[];
  selectedTemplateId: string;
  setSelectedTemplateId: Dispatch<SetStateAction<string>>;
  templates: ContractTemplate[];
  voipNumbers: VoipNumber[];
  setVoipNumbers: Dispatch<SetStateAction<VoipNumber[]>>;
  voipOperators: VoipOperator[];
  voipWantsNumbers: 'unset' | 'yes' | 'no';
  setVoipWantsNumbers: Dispatch<SetStateAction<'unset' | 'yes' | 'no'>>;
  selectedTemplateIsAddon: boolean;
  parentContractId: string;
  setParentContractId: Dispatch<SetStateAction<string>>;
  parentContracts: Contract[];
  addonServices: AddonServices;
  setAddonServices: Dispatch<SetStateAction<AddonServices>>;
  addonPaymentDays: number;
  setAddonPaymentDays: Dispatch<SetStateAction<number>>;
  addonSchedulingDays: number;
  setAddonSchedulingDays: Dispatch<SetStateAction<number>>;
  addonTitle: string;
  setAddonTitle: Dispatch<SetStateAction<string>>;
  addonSubtitle: string;
  setAddonSubtitle: Dispatch<SetStateAction<string>>;
  selectedQuoteData: Quote | null;
  quoteTotal: number;
  loadingPaymentInfo: boolean;
  quotePaymentInfo: QuotePaymentInfo | null;
  installmentPlan: Installment[];
  durationMonths: number;
  onDurationChange: (months: number) => void;
  startDate: string;
  onStartDateChange: (date: string) => void;
  endDate: string;
  installmentFrequency: Frequency;
  onFrequencyChange: (freq: Frequency) => void;
  addInstallment: () => void;
  updateInstallment: (index: number, field: string, value: string | number) => void;
  removeInstallment: (index: number) => void;
  planValid: boolean;
  planTotal: number;
  planError: string;
  dateAdjustPending: DateAdjustPending;
  applyDateAdjustment: (adjustSubsequent: boolean) => void;
  saving: boolean;
  handleCreate: () => Promise<void>;
}

export function CreateContractModal({
  showCreate, setShowCreate, selectedQuoteId, onQuoteSelected, acceptedQuotes,
  selectedTemplateId, setSelectedTemplateId, templates,
  voipNumbers, setVoipNumbers, voipOperators, voipWantsNumbers, setVoipWantsNumbers,
  selectedTemplateIsAddon, parentContractId, setParentContractId, parentContracts,
  addonServices, setAddonServices, addonPaymentDays, setAddonPaymentDays,
  addonSchedulingDays, setAddonSchedulingDays, addonTitle, setAddonTitle, addonSubtitle, setAddonSubtitle,
  selectedQuoteData, quoteTotal, loadingPaymentInfo, quotePaymentInfo, installmentPlan,
  durationMonths, onDurationChange, startDate, onStartDateChange, endDate,
  installmentFrequency, onFrequencyChange, addInstallment, updateInstallment, removeInstallment,
  planValid, planTotal, planError, dateAdjustPending, applyDateAdjustment, saving, handleCreate,
}: CreateContractModalProps) {
  return (
      <ModalWrapper open={showCreate} onClose={() => setShowCreate(false)} title="Nuovo Contratto da Preventivo" wide>
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Preventivo Accettato" value={selectedQuoteId} onChange={onQuoteSelected}
              options={[{ value: '', label: 'Seleziona...' }, ...acceptedQuotes.map(q => ({ value: q.id, label: `${q.quote_number} - ${q.tenant_name || ''} (€${Number(q.total_gross || '0').toFixed(2)})${q.status === 'payment_confirmed' ? ' ✅ Pagamento' : q.status === 'payment_setup_sent' ? ' ⏳ In attesa pagamento' : ''}` }))]} />
            <SelectField label="Template Contratto" value={selectedTemplateId} onChange={(v: string) => { setSelectedTemplateId(v); const t = templates.find(x => x.id === v); if (!t?.requires_voip_attachments) { setVoipNumbers([]); setVoipWantsNumbers('unset'); } }}
              options={[{ value: '', label: 'Template Default' }, ...templates.map(t => ({ value: t.id, label: `${t.name}${t.is_default ? ' ★' : ''}${t.requires_voip_attachments ? ' 📞' : ''}${t.is_addon ? ' 🔗 Addon' : ''}` }))]} />
          </div>

          {(() => {
            const tpl = templates.find(t => t.id === selectedTemplateId);
            if (!tpl?.requires_voip_attachments) return null;
            const wantsList = voipWantsNumbers === 'yes';
            return (
              <div className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="text-foreground dark:text-white font-medium flex items-center gap-2 mb-2">📞 Allega numerazioni VoIP al contratto?</h4>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setVoipWantsNumbers('yes'); if (voipNumbers.length === 0) setVoipNumbers([{ type: 'new', number: '' }]); }}
                      className={`px-3 py-1.5 rounded text-sm font-medium border ${voipWantsNumbers === 'yes' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-700 text-foreground dark:text-foreground border-border dark:border-gray-600'}`}>Sì, gestisci numeri</button>
                    <button type="button" onClick={() => { setVoipWantsNumbers('no'); setVoipNumbers([]); }}
                      className={`px-3 py-1.5 rounded text-sm font-medium border ${voipWantsNumbers === 'no' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-700 text-foreground dark:text-foreground border-border dark:border-gray-600'}`}>No, nessun numero ora</button>
                  </div>
                  {voipWantsNumbers === 'no' && <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-2">Il bundle viene generato senza tabella numerazioni. Puoi aggiungerle in seguito.</p>}
                </div>
                {!wantsList ? null : (<>
                <div className="flex items-center justify-between mb-3 pt-2 border-t border-border dark:border-gray-700">
                  <span className="text-xs text-muted-foreground dark:text-muted-foreground/70">Max 50 numerazioni. Per portabilità (GNP) operatore e codice migrazione sono obbligatori.</span>
                  <button type="button" onClick={() => setVoipNumbers(prev => prev.length >= 50 ? prev : [...prev, { type: 'new', number: '' }])}
                    className="text-blue-500 hover:text-blue-400 text-sm flex items-center gap-1"><Plus className="h-3 w-3" /> Aggiungi numero</button>
                </div>
                {voipNumbers.length === 0 ? (
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Nessuna numerazione aggiunta.</p>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {voipNumbers.map((n, i) => (
                      <div key={i} className="bg-muted/50 dark:bg-gray-700/50 rounded p-2 space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                          <span className="md:col-span-1 text-muted-foreground dark:text-muted-foreground/70 text-sm font-mono">#{i + 1}</span>
                          <select value={n.type} onChange={(e) => setVoipNumbers(prev => prev.map((x, j) => j === i ? { ...x, type: e.target.value as 'new' | 'portability', operator_code: e.target.value === 'portability' ? (x.operator_code || '') : undefined } : x))}
                            className="md:col-span-3 bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1 text-sm">
                            <option value="new">Nuova attivazione</option>
                            <option value="portability">Portabilità (GNP)</option>
                          </select>
                          <input type="text" value={n.number} placeholder="es. +390825..." onChange={(e) => setVoipNumbers(prev => prev.map((x, j) => j === i ? { ...x, number: e.target.value } : x))}
                            className="md:col-span-4 bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1 text-sm font-mono" />
                          {n.type === 'portability' ? (
                            <>
                              <input list={`voip-ops-${i}`} value={voipOperators.find(o => o.code === n.operator_code)?.label || n.operator_code || ''}
                                onChange={(e) => { const v = e.target.value; const found = voipOperators.find(o => o.label.toLowerCase() === v.toLowerCase() || o.code === v); setVoipNumbers(prev => prev.map((x, j) => j === i ? { ...x, operator_code: found ? found.code : v } : x)); }}
                                placeholder="Cerca operatore..."
                                className="md:col-span-3 bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1 text-sm" />
                              <datalist id={`voip-ops-${i}`}>{voipOperators.map(o => <option key={o.code} value={o.label}>{o.code}</option>)}</datalist>
                            </>
                          ) : <div className="md:col-span-3" />}
                          <button type="button" onClick={() => setVoipNumbers(prev => prev.filter((_, j) => j !== i))}
                            className="md:col-span-1 text-muted-foreground/70 hover:text-red-400 p-1 justify-self-end"><Trash2 className="h-3 w-3" /></button>
                        </div>
                        {n.type === 'portability' && (
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 pl-0 md:pl-10">
                            {n.operator_code === 'OTHER' && (
                              <input type="text" value={n.operator_other || ''} placeholder="Specifica operatore..." onChange={(e) => setVoipNumbers(prev => prev.map((x, j) => j === i ? { ...x, operator_other: e.target.value } : x))}
                                className="md:col-span-4 bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1 text-xs" />
                            )}
                            <input type="text" required value={n.codice_migrazione || ''} placeholder="Codice migrazione * (8-32 alfanum.)" onChange={(e) => setVoipNumbers(prev => prev.map((x, j) => j === i ? { ...x, codice_migrazione: e.target.value.toUpperCase() } : x))}
                              className={`${n.operator_code === 'OTHER' ? 'md:col-span-3' : 'md:col-span-4'} bg-white dark:bg-gray-700 border ${(n.codice_migrazione && /^[A-Z0-9]{8,32}$/.test(n.codice_migrazione)) ? 'border-border dark:border-gray-600' : 'border-orange-400 dark:border-orange-500'} text-foreground dark:text-white rounded px-2 py-1 text-xs font-mono`} />
                            <input type="text" value={n.note || ''} placeholder="Note (opz.)" onChange={(e) => setVoipNumbers(prev => prev.map((x, j) => j === i ? { ...x, note: e.target.value } : x))}
                              className="md:col-span-4 bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded px-2 py-1 text-xs" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                </>)}
              </div>
            );
          })()}

          {selectedTemplateIsAddon && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-300 dark:border-indigo-700 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300 font-medium">
                <span>🔗 Contratto Addizionale</span>
                <span className="text-xs text-indigo-500 dark:text-indigo-400 font-normal">— collegato a un contratto madre firmato</span>
              </div>
              <SelectField label="Contratto Madre (firmato)" value={parentContractId} onChange={setParentContractId}
                options={[
                  { value: '', label: parentContracts.length === 0 ? 'Nessun contratto firmato disponibile per questo cliente' : 'Seleziona contratto madre...' },
                  ...parentContracts.map((c) => ({
                    value: c.id,
                    label: `${c.contract_number} — firmato ${c.signed_at ? new Date(c.signed_at).toLocaleDateString('it-IT') : 'N/D'}`
                  }))
                ]} />
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-2">Tipo di servizio (selezionabili più di uno)</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { k: 'setup', label: 'Setup', color: 'green' },
                    { k: 'formazione', label: 'Formazione', color: 'purple' },
                    { k: 'assistenza', label: 'Assistenza', color: 'orange' },
                  ] as const).map(({ k, label }) => (
                    <label key={k} className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer ${addonServices[k] ? 'bg-indigo-100 dark:bg-indigo-800/40 border-indigo-400' : 'bg-white dark:bg-gray-800 border-border dark:border-gray-600'}`}>
                      <input type="checkbox" checked={addonServices[k]} onChange={(e) => setAddonServices(s => ({ ...s, [k]: e.target.checked }))} />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-1">Giorni per pagamento (1-90)</label>
                  <input type="number" min={1} max={90} value={addonPaymentDays}
                    onChange={(e) => setAddonPaymentDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 15)))}
                    className="w-full px-3 py-2 rounded border border-border dark:border-gray-600 bg-white dark:bg-gray-800 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-1">Giorni per pianificazione (1-90)</label>
                  <input type="number" min={1} max={90} value={addonSchedulingDays}
                    onChange={(e) => setAddonSchedulingDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 10)))}
                    className="w-full px-3 py-2 rounded border border-border dark:border-gray-600 bg-white dark:bg-gray-800 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-1">Titolo (opzionale, max 100)</label>
                  <input type="text" maxLength={100} value={addonTitle}
                    onChange={(e) => setAddonTitle(e.target.value)}
                    placeholder="Lasciare vuoto per derivare automaticamente dai servizi selezionati"
                    className="w-full px-3 py-2 rounded border border-border dark:border-gray-600 bg-white dark:bg-gray-800 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-1">Sottotitolo (opzionale, max 200)</label>
                  <input type="text" maxLength={200} value={addonSubtitle}
                    onChange={(e) => setAddonSubtitle(e.target.value)}
                    placeholder="Lasciare vuoto per il sottotitolo predefinito"
                    className="w-full px-3 py-2 rounded border border-border dark:border-gray-600 bg-white dark:bg-gray-800 text-sm" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Modalità di pagamento derivata automaticamente dal metodo configurato sul cliente (carta/Stripe se disponibile, altrimenti bonifico).</p>
            </div>
          )}

          {selectedQuoteData && (
            <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-foreground dark:text-white font-medium">Riepilogo Preventivo</h4>
                <span className="text-foreground dark:text-white font-bold">Totale: € {quoteTotal.toFixed(2)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div><span className="text-muted-foreground dark:text-muted-foreground/70">Netto:</span> <span className="text-foreground dark:text-white">€ {Number(selectedQuoteData.subtotal_net ?? 0).toFixed(2)}</span></div>
                <div><span className="text-muted-foreground dark:text-muted-foreground/70">IVA:</span> <span className="text-foreground dark:text-white">€ {Number(selectedQuoteData.total_vat ?? 0).toFixed(2)}</span></div>
                {Number(selectedQuoteData.entry_fee ?? 0) > 0 && (
                  <div><span className="text-muted-foreground dark:text-muted-foreground/70">Entry Fee:</span> <span className="text-foreground dark:text-white">€ {Number(selectedQuoteData.entry_fee ?? 0).toFixed(2)}</span></div>
                )}
              </div>
            </div>
          )}

          {selectedQuoteId && loadingPaymentInfo && (
            <div className="flex items-center justify-center py-6 text-muted-foreground dark:text-muted-foreground/70">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Verifica stato pagamento...
            </div>
          )}

          {selectedQuoteId && !loadingPaymentInfo && quotePaymentInfo?.has_payment_plan && quotePaymentInfo?.payment_method_configured && (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <h4 className="text-green-700 dark:text-green-400 font-semibold">Pagamento Configurato</h4>
                </div>
                <p className="text-green-600 dark:text-green-400 text-sm">Il metodo di pagamento è stato configurato e il piano rateale è già stato accettato dal cliente. Il contratto utilizzerà il piano rateale esistente.</p>
              </div>
              <div className="bg-muted/50 dark:bg-gray-700/50 border border-border dark:border-gray-600 rounded-lg p-4">
                <h4 className="text-foreground dark:text-white font-medium flex items-center gap-2 mb-3"><Calendar className="h-4 w-4" /> Piano Rateale Confermato</h4>
                <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                  {quotePaymentInfo.duration_months && <div><span className="text-muted-foreground dark:text-muted-foreground/70">Durata:</span> <span className="text-foreground dark:text-white font-medium">{quotePaymentInfo.duration_months} mesi</span></div>}
                  {quotePaymentInfo.start_date && <div><span className="text-muted-foreground dark:text-muted-foreground/70">Dal:</span> <span className="text-foreground dark:text-white font-medium">{new Date(quotePaymentInfo.start_date).toLocaleDateString('it-IT')}</span></div>}
                  {quotePaymentInfo.end_date && <div><span className="text-muted-foreground dark:text-muted-foreground/70">Al:</span> <span className="text-foreground dark:text-white font-medium">{new Date(quotePaymentInfo.end_date).toLocaleDateString('it-IT')}</span></div>}
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {installmentPlan.map((inst, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-white dark:bg-gray-800 rounded text-sm">
                      <span className="text-muted-foreground dark:text-muted-foreground/70 font-mono">Rata #{inst.number}</span>
                      <span className="text-foreground dark:text-muted-foreground/60">{new Date(inst.due_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                      <span className="text-foreground dark:text-white font-semibold">€ {inst.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedQuoteId && !loadingPaymentInfo && quotePaymentInfo && (!quotePaymentInfo.has_payment_plan || !quotePaymentInfo.payment_method_configured) && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <h4 className="text-amber-700 dark:text-amber-400 font-semibold">Pagamento Non Completato</h4>
              </div>
              <p className="text-amber-600 dark:text-amber-400 text-sm">
                {!quotePaymentInfo.has_payment_plan
                  ? 'Il piano rateale non è ancora stato configurato per questo preventivo. Invia prima il setup pagamento dalla tab Preventivi.'
                  : 'Il metodo di pagamento non è ancora stato configurato dal cliente. Il contratto potrà essere inviato solo dopo la configurazione del pagamento.'}
              </p>
            </div>
          )}

          {selectedQuoteId && !loadingPaymentInfo && !quotePaymentInfo && (
            <>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-foreground dark:text-white font-medium flex items-center gap-2 mb-3"><Clock className="h-4 w-4" /> Durata Contrattuale</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-muted-foreground dark:text-muted-foreground/70 text-xs mb-1">Durata (mesi)</label>
                    <select value={durationMonths} onChange={(e) => onDurationChange(parseInt(e.target.value))}
                      className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
                      {[1,3,6,12,18,24,36,48,60].map(m => (
                        <option key={m} value={m}>{m} {m === 1 ? 'mese' : 'mesi'}{m === 12 ? ' (1 anno)' : m === 24 ? ' (2 anni)' : m === 36 ? ' (3 anni)' : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-muted-foreground dark:text-muted-foreground/70 text-xs mb-1">Decorrenza</label>
                    <input type="date" value={startDate} onChange={(e) => onStartDateChange(e.target.value)}
                      className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-muted-foreground dark:text-muted-foreground/70 text-xs mb-1">Scadenza</label>
                    <input type="date" value={endDate} readOnly
                      className="w-full bg-muted dark:bg-gray-600 border border-border dark:border-gray-600 text-muted-foreground dark:text-muted-foreground/70 rounded-lg px-3 py-2 text-sm cursor-not-allowed" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-foreground dark:text-white font-medium flex items-center gap-2"><Calendar className="h-4 w-4" /> Piano Rateale</h4>
                  <div className="flex items-center gap-3">
                    <select value={installmentFrequency} onChange={(e) => onFrequencyChange(e.target.value as 'monthly' | 'quarterly' | 'semiannual' | 'annual')}
                      className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1 text-xs">
                      <option value="monthly">Mensile</option>
                      <option value="quarterly">Trimestrale</option>
                      <option value="semiannual">Semestrale</option>
                      <option value="annual">Annuale</option>
                    </select>
                    <button onClick={addInstallment} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"><Plus className="h-3 w-3" /> Aggiungi Rata</button>
                  </div>
                </div>
                <div className="text-muted-foreground dark:text-muted-foreground/70 text-xs mb-2">
                  {installmentPlan.length} rate — Importo rata: € {installmentPlan.length > 0 ? (quoteTotal / installmentPlan.length).toFixed(2) : '0.00'} — Modifica un importo per ridistribuire le altre rate automaticamente
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {installmentPlan.map((inst, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm w-8 text-center font-mono">#{inst.number}</span>
                      <input type="date" value={inst.due_date}
                        onChange={(e) => updateInstallment(i, 'due_date', e.target.value)}
                        className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm flex-1" />
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">€</span>
                        <input type="number" step="0.01" value={inst.amount}
                          onChange={(e) => updateInstallment(i, 'amount', e.target.value)}
                          className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm w-28" />
                      </div>
                      <input type="text" value={inst.description} placeholder="Note..."
                        onChange={(e) => updateInstallment(i, 'description', e.target.value)}
                        className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm flex-1" />
                      {installmentPlan.length > 1 && (
                        <button onClick={() => removeInstallment(i)} className="text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-3 w-3" /></button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border dark:border-gray-700">
                  <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">{installmentPlan.length} rate</span>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${planValid ? 'text-green-400' : 'text-red-400'}`}>
                      Totale Rate: € {planTotal.toFixed(2)} / € {quoteTotal.toFixed(2)}
                    </span>
                    {planValid && <CheckCircle className="h-4 w-4 text-green-400" />}
                    {!planValid && installmentPlan.length > 0 && <AlertTriangle className="h-4 w-4 text-red-400" />}
                  </div>
                </div>
                {planError && <p className="text-red-400 text-sm mt-2">{planError}</p>}

                {dateAdjustPending && (
                  <div className="mt-3 bg-blue-900/30 border border-blue-600 rounded-lg p-3">
                    <p className="text-blue-300 text-sm mb-2">
                      Hai spostato la rata #{installmentPlan[dateAdjustPending.index]?.number} di <strong>{Math.abs(dateAdjustPending.deltaDays)} giorni</strong> {dateAdjustPending.deltaDays > 0 ? 'in avanti' : 'indietro'}.
                      Vuoi adattare anche le date delle rate successive?
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => applyDateAdjustment(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">
                        Sì, adatta tutte
                      </button>
                      <button onClick={() => applyDateAdjustment(false)}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm">
                        No, solo questa
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
            <button onClick={handleCreate}
              disabled={saving || !selectedQuoteId || loadingPaymentInfo || (quotePaymentInfo && (!quotePaymentInfo.payment_method_configured || !quotePaymentInfo.has_payment_plan)) || (!quotePaymentInfo && installmentPlan.length > 0 && !planValid)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              title={quotePaymentInfo && !quotePaymentInfo.payment_method_configured ? 'Il metodo di pagamento non è ancora configurato' : quotePaymentInfo && !quotePaymentInfo.has_payment_plan ? 'Il piano rateale non è stato ancora configurato' : ''}>
              {saving ? 'Creazione...' : 'Crea e Invia Contratto'}
            </button>
          </div>
        </div>
      </ModalWrapper>
  );
}
